// SPDX-License-Identifier: AGPL-3.0-or-later

import {makeAutoObservable, observable, runInAction} from 'mobx';

const MIN_UNIX_SECONDS = 1_000_000_000;

function sanitizeOccupiedSinceUnix(value: unknown): number | null {
	let unix: number | null = null;
	if (typeof value === 'number' && Number.isFinite(value)) {
		unix = Math.floor(value);
	} else if (typeof value === 'string' && /^\d{10,16}$/.test(value)) {
		unix = Number(value);
	}
	if (unix == null || !Number.isFinite(unix)) {
		return null;
	}
	if (unix > 1_000_000_000_000) {
		unix = Math.floor(unix / 1000);
	}
	const now = Math.floor(Date.now() / 1000);
	if (unix < MIN_UNIX_SECONDS || unix > now + 60) {
		return null;
	}
	return unix;
}

/** Prefer channel occupancy fields; never use bare started_at (activity collision risk). */
function occupancyUnixFromPayload(value: {
	channel_occupied_since?: unknown;
	occupied_since?: unknown;
	started_at?: unknown;
}): number | null {
	return (
		sanitizeOccupiedSinceUnix(value.channel_occupied_since) ??
		sanitizeOccupiedSinceUnix(value.occupied_since) ??
		sanitizeOccupiedSinceUnix(value.started_at)
	);
}

class VoiceChannelOccupancyStoreImpl {
	private occupiedSinceByChannel = observable.object<Record<string, Record<string, number>>>({});

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	getOccupiedSinceUnix(guildId: string, channelId: string): number | null {
		const guildMap = this.occupiedSinceByChannel[guildId];
		if (!guildMap) {
			return null;
		}
		return guildMap[channelId] ?? guildMap[String(channelId)] ?? null;
	}

	/**
	 * The gateway owns the channel's "occupied since" (set by the first joiner, kept until the channel
	 * empties), so its value always wins. A local seed is only a placeholder until that value arrives.
	 */
	setOccupiedSince(guildId: string, channelId: string, sinceUnix: number, authoritative = true): void {
		const sanitized = sanitizeOccupiedSinceUnix(sinceUnix);
		if (sanitized == null) {
			return;
		}
		const key = String(channelId);
		const existing = this.getOccupiedSinceUnix(guildId, key);
		if (existing === sanitized || (!authoritative && existing != null)) {
			return;
		}
		runInAction(() => {
			const guildMap = {...(this.occupiedSinceByChannel[guildId] ?? {})};
			guildMap[key] = sanitized;
			this.occupiedSinceByChannel = {...this.occupiedSinceByChannel, [guildId]: guildMap};
		});
	}

	clearChannel(guildId: string, channelId: string): void {
		const guildMap = this.occupiedSinceByChannel[guildId];
		if (!guildMap?.[channelId] && !guildMap?.[String(channelId)]) {
			return;
		}
		runInAction(() => {
			const nextGuildMap = {...guildMap};
			delete nextGuildMap[channelId];
			delete nextGuildMap[String(channelId)];
			if (Object.keys(nextGuildMap).length === 0) {
				const next = {...this.occupiedSinceByChannel};
				delete next[guildId];
				this.occupiedSinceByChannel = next;
			} else {
				this.occupiedSinceByChannel = {...this.occupiedSinceByChannel, [guildId]: nextGuildMap};
			}
		});
	}

	clearGuild(guildId: string): void {
		if (!this.occupiedSinceByChannel[guildId]) {
			return;
		}
		runInAction(() => {
			const next = {...this.occupiedSinceByChannel};
			delete next[guildId];
			this.occupiedSinceByChannel = next;
		});
	}

	syncLocalCount(guildId: string, channelId: string, userCount: number): void {
		if (userCount <= 0) {
			this.clearChannel(guildId, channelId);
		}
	}

	seedIfChannelBecameOccupied(guildId: string, channelId: string): void {
		if (this.getOccupiedSinceUnix(guildId, channelId) != null) {
			return;
		}
		this.setOccupiedSince(guildId, channelId, Math.floor(Date.now() / 1000), false);
	}

	hydrateFromGuild(guild: {
		id: string;
		voice_channel_occupied_since?: unknown;
		voice_states?: ReadonlyArray<{
			channel_id?: string | null;
			started_at?: unknown;
			channel_occupied_since?: unknown;
			occupied_since?: unknown;
		}>;
		channels?: ReadonlyArray<{id: string; occupied_since?: unknown; channel_occupied_since?: unknown}>;
	}): void {
		this.syncFromOccupancyMap(guild.id, guild.voice_channel_occupied_since);
		this.syncFromVoiceStates(guild.id, guild.voice_states ?? []);
		for (const channel of guild.channels ?? []) {
			this.syncFromVoiceState(guild.id, channel.id, channel.channel_occupied_since ?? channel.occupied_since);
		}
	}

	syncFromOccupancyMap(guildId: string, occupancy: unknown): void {
		if (occupancy == null) {
			return;
		}
		if (Array.isArray(occupancy)) {
			for (const entry of occupancy) {
				if (entry == null || typeof entry !== 'object' || Array.isArray(entry)) {
					continue;
				}
				const record = entry as Record<string, unknown>;
				const channelId = record.channel_id;
				if (typeof channelId !== 'string' && typeof channelId !== 'number') {
					continue;
				}
				const since = occupancyUnixFromPayload({
					channel_occupied_since: record.channel_occupied_since,
					occupied_since: record.occupied_since,
					started_at: record.started_at,
				});
				this.syncFromVoiceState(guildId, String(channelId), since);
			}
			return;
		}
		if (typeof occupancy !== 'object') {
			return;
		}
		for (const [channelId, occupiedSince] of Object.entries(occupancy as Record<string, unknown>)) {
			this.syncFromVoiceState(guildId, channelId, occupiedSince);
		}
	}

	syncFromVoiceState(guildId: string, channelId: string | null | undefined, occupiedSince: unknown): void {
		if (!channelId) {
			return;
		}
		const since = sanitizeOccupiedSinceUnix(occupiedSince);
		if (since == null) {
			return;
		}
		this.setOccupiedSince(guildId, channelId, since);
	}

	syncFromVoiceStates(
		guildId: string,
		voiceStates: ReadonlyArray<{
			channel_id?: string | null;
			started_at?: unknown;
			channel_occupied_since?: unknown;
			occupied_since?: unknown;
		}>,
	): void {
		// All occupants of a channel carry the same server stamp; take the earliest per channel so a
		// single not-yet-restamped state cannot make the timer flip between values.
		const earliest = new Map<string, number>();
		for (const voiceState of voiceStates) {
			const since = occupancyUnixFromPayload(voiceState);
			if (!voiceState.channel_id || since == null) continue;
			const current = earliest.get(voiceState.channel_id);
			if (current == null || since < current) earliest.set(voiceState.channel_id, since);
		}
		for (const [channelId, since] of earliest) {
			this.syncFromVoiceState(guildId, channelId, since);
		}
	}
}

const VoiceChannelOccupancyStore = new VoiceChannelOccupancyStoreImpl();
export default VoiceChannelOccupancyStore;
export {sanitizeOccupiedSinceUnix};
